import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';

/** Org, project, and revision identifiers used for sidebar and command-bar deep links. */
export interface NavigationLinkParams {
	orgName?: string;
	projectId?: string;
	revisionId?: string;
}

/**
 * Reads a route param from the current URL, falling back to last-open project and latest revision.
 *
 * @param paramName - Router param name (e.g. `projectId`).
 * @param urlParams - Params from `useParams({ strict: false })`.
 * @param linkParams - Resolved navigation context from {@link useNavigationLinkParams}.
 * @returns Param value when available, otherwise `undefined`.
 */
export function getNavigationParamValue(
	paramName: string,
	urlParams: Record<string, unknown>,
	linkParams: NavigationLinkParams,
): string | undefined {
	const fromUrl = urlParams[paramName];
	if (typeof fromUrl === 'string' && fromUrl) {
		return fromUrl;
	}

	const fromContext = linkParams[paramName as keyof NavigationLinkParams];
	if (fromContext) {
		return fromContext;
	}

	return undefined;
}

/**
 * Returns whether a route param can be satisfied from the URL or navigation context.
 *
 * @param paramName - Router param name.
 * @param urlParams - Params from `useParams({ strict: false })`.
 * @param linkParams - Resolved navigation context.
 * @returns `true` when the param has a non-empty value.
 */
export function hasNavigationParam(
	paramName: string,
	urlParams: Record<string, unknown>,
	linkParams: NavigationLinkParams,
): boolean {
	return getNavigationParamValue(paramName, urlParams, linkParams) !== undefined;
}

/**
 * Resolves org, project, and revision params for navigation links (sidebar, command bar).
 * Mirrors sidebar behavior: URL params take precedence, then last-open project and latest revision.
 *
 * @returns Resolved `orgName`, `projectId`, and `revisionId` when available.
 */
export function useNavigationLinkParams(): NavigationLinkParams {
	const params = useParams({ strict: false });
	const lastOpenProject = useAtomValue(lastOpenProjectAtom);
	const trpc = useTRPC();

	const orgName = getNavigationParamValue('orgName', params, {
		orgName: lastOpenProject.orgName ?? undefined,
	});

	// Only fall back to the last-open project when it belongs to the org we're
	// resolving links for. A project left over from a previously-viewed org would
	// otherwise drive cross-org `getLatest`/`getPreview` queries the current user
	// isn't authorized to make.
	const lastOpenProjectId = lastOpenProject.orgName === orgName ? lastOpenProject.projectId : null;
	const projectId = getNavigationParamValue('projectId', params, {
		projectId: lastOpenProjectId ?? undefined,
	});

	const { data: latestRevision } = useQuery(
		trpc.revision.getLatest.queryOptions({ projectId: projectId ?? '' }, { enabled: !!projectId, throwOnError: false }),
	);

	const revisionId = getNavigationParamValue('revisionId', params, {
		revisionId: latestRevision?.revision?.id,
	});

	return useMemo(() => ({ orgName, projectId, revisionId }), [orgName, projectId, revisionId]);
}
